import { IconButton, Select, TextField } from '@nike/eds'
import { useId } from 'react'

type PaginationControlsProps = {
  table: {
    setPageIndex: (index: number) => void
    previousPage: () => void
    nextPage: () => void
    getCanPreviousPage: () => boolean
    getCanNextPage: () => boolean
    getPageCount: () => number
    setPageSize: (size: number) => void
    getFilteredRowModel: () => { rows: any[] }
    getRowCount: () => number
    getState: () => {
      pagination: {
        pageIndex: number
        pageSize: number
      }
    }
  }
  pageSizeOptions: number[]
  totalCount?: number
}

export function PaginationControls({ table, pageSizeOptions, totalCount }: PaginationControlsProps) {
  const currentPage = table.getState().pagination.pageIndex
  const pageSize = table.getState().pagination.pageSize
  const totalItems = totalCount || table.getRowCount()
  const startIndex = currentPage * pageSize + 1
  const endIndex = Math.min((currentPage + 1) * pageSize, totalItems)

  const formattedPageSizeOptions = pageSizeOptions.map((size) => ({
    value: size,
    label: size,
  }))

  const uniqueId = useId()
  const pageIndexInputId = `page-index-input-${uniqueId}`
  const pageSizeSelectId = `page-size-select-${uniqueId}`

  const pageSizeNumber = Number(pageSize)
  const selectedOption = formattedPageSizeOptions.find(
    (option) => Number(option.value) === pageSizeNumber
  )

  if (totalItems === 0) {
    return null
  }

  return (
    <div className={'paginationToolbar'}>
      <IconButton
        className={'paginationControls'}
        icon='CaretLeft'
        variant='ghost'
        label='Previous page'
        onClick={() => table.previousPage()}
        disabled={!table.getCanPreviousPage()}
        disableRipple
      />
      <TextField
        key={currentPage}
        id={pageIndexInputId}
        defaultValue={currentPage + 1}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const newPageIndex = Math.max(0, Math.min(table.getPageCount() - 1, Number(e.currentTarget.value) - 1))
            if (newPageIndex < table.getPageCount()) {
              table.setPageIndex(newPageIndex)
            }
          }
        }}
        aria-describedby='page-index-input-description'
        label='Page'
        hideLabel
        style={{ width: '60px' }}
      />
      <IconButton
        className={'paginationControls'}
        icon='CaretRight'
        label='Next page'
        variant='ghost'
        onClick={() => table.nextPage()}
        disabled={!table.getCanNextPage()}
        disableRipple
      />

      <div>Results:</div>

      <Select
        id={pageSizeSelectId}
        options={formattedPageSizeOptions}
        value={selectedOption}
        onChange={(selected: any) => table.setPageSize(Number(selected.value))}
        hideLabel
        label=''
        menuPosition='fixed'
      />

      <span>
        {startIndex}-{endIndex} of {totalItems}
      </span>
    </div>
  )
}
